import _ from 'underscore';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  PrimaryButton,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
} from '@fluentui/react';
import { Input } from '@bingads-webui-react/primitive';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getManuallyAddBrandStyles } from '../../style';

const brandError = {
  tooManyWords: _TL_('Too many words in brand name (> 10)'),
  tooLong: _TL_('Should not exceed 256 characters'),
  duplicatedBrandName: _TL_('Brand name already exists'),
};

const ManuallyAddBrandModalUnstyled = (props) => {
  const {
    i18n,
    showDialog,
    setShowDialog,
    setBrand,
    classes,
    brandsList,
    selectedBrands,
  } = props;

  const [brandName, setBrandName] = useState('');
  const [brandDomain, setBrandDomain] = useState('');
  const [brandNameError, setBrandNameError] = useState('');

  const i18nStrings = useMemo(() => ({
    brandName: i18n.getString(_TL_('Brand name')),
    brandDomain: i18n.getString(_TL_('Brand domain')),
  }), [i18n]);

  const dialogContentProps = {
    type: DialogType.normal,
    title: i18n.getString(_TL_('Manually add brand')),
    closeButtonAriaLabel: 'Close',
  };

  const resetState = useCallback(() => {
    setBrandName('');
    setBrandDomain('');
    setBrandNameError('');
  }, [setBrandName, setBrandDomain]);

  const onBrandNameChange = useCallback((brandNameValue) => {
    const brandNameTrim = brandNameValue.trim();
    if (brandNameTrim.length > 256) {
      setBrandNameError(brandError.tooLong);
    } else if (_.size(brandNameTrim.split(' ')) > 10) {
      setBrandNameError(brandError.tooManyWords);
    } else {
      setBrandNameError('');
    }
    setBrandName(brandNameTrim);
  }, [setBrandName]);

  const validateDuplicated = useCallback(() => {
    const hasDuplicated = _.findIndex([...selectedBrands, ...brandsList], brand => brand.BrandName === brandName) !== -1;
    if (hasDuplicated) {
      setBrandNameError(brandError.duplicatedBrandName);
      return false;
    }
    return true;
  }, [selectedBrands, brandsList, brandName]);

  // For now no need to validate brand domain
  const onBrandDomainChange = useCallback((brandDomainValue) => {
    setBrandDomain(brandDomainValue.trim());
  }, [setBrandDomain]);

  const saveBrand = useCallback(() => {
    if (!validateDuplicated()) return;
    setBrand({
      Id: brandName,
      BrandName: brandName,
      BrandUrl: brandDomain,
    });
    setShowDialog(false);
  }, [brandName, brandDomain, setBrand, setShowDialog, validateDuplicated]);

  const canSaveBrand = useMemo(
    () => !_.isEmpty(brandName) && !_.isEmpty(brandDomain) && _.isEmpty(brandNameError),
    [brandName, brandDomain, brandNameError]
  );

  useEffect(() => {
    if (showDialog) {
      resetState();
    }
  }, [showDialog, resetState]);

  return (
    <Dialog
      hidden={!showDialog}
      onDismiss={setShowDialog}
      dialogContentProps={dialogContentProps}
      minWidth={450}
    >
      <div className="manually-add-brand-modal">
        <div className={classes.brandFormItem}>
          <b className={classes.brandLabel}>{ i18nStrings.brandName } </b>
          <Input
            className={`${classes.brandInfoInput} brand-name-input`}
            aria-label={i18nStrings.brandName}
            onChange={e => onBrandNameChange(e.currentTarget.value)}
          />
          { brandNameError && <div className={`${classes.errorHint} brand-name-error`}>{i18n.getString(brandNameError)}</div> }
        </div>
        <div className={classes.brandFormItem}>
          <b className={classes.brandLabel}>{ i18nStrings.brandDomain } </b>
          <Input
            className={`${classes.brandInfoInput} brand-domain-input`}
            aria-label={i18nStrings.brandDomain}
            placeholder="https://www.example.com"
            onChange={e => onBrandDomainChange(e.currentTarget.value)}
          />
        </div>
        <div className="alert alert-info">
          <div className="alert-image" />
          <div className="alert-info-inline">
            { i18n.getString(_TL_('We will do our best to match your info with products from retailers in our database.'))}
          </div>
        </div>
      </div>
      <DialogFooter>
        <PrimaryButton className="manually-save-button" onClick={saveBrand} text={i18n.getString(_TL_('Add'))} disabled={!canSaveBrand} />
        <DefaultButton className="manually-cancel-button" onClick={() => setShowDialog(false)} text={i18n.getString(_TL_('Cancel'))} />
      </DialogFooter>
    </Dialog>
  );
};

ManuallyAddBrandModalUnstyled.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  showDialog: PropTypes.bool.isRequired,
  setShowDialog: PropTypes.func.isRequired,
  setBrand: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    brandLabel: PropTypes.string.isRequired,
    brandInfoInput: PropTypes.string.isRequired,
    brandFormItem: PropTypes.string.isRequired,
    errorHint: PropTypes.string.isRequired,
  }).isRequired,
  brandsList: PropTypes.arrayOf(PropTypes.shape({
    BrandName: PropTypes.string.isRequired,
  })).isRequired,
  selectedBrands: PropTypes.arrayOf(PropTypes.shape({
    BrandName: PropTypes.string.isRequired,
  })).isRequired,
};

export const ManuallyAddBrandModal = withDefaultStyles(ManuallyAddBrandModalUnstyled, getManuallyAddBrandStyles);
